import {
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http'
import { Injectable } from '@angular/core'
import { AdvancedTableApiService } from '@mmx/components'
import { AuthenticatedRestService, ReferrerModel, truthyValues } from '@mmx/shared'
import {
  BehaviorSubject,
  catchError,
  concatMap,
  exhaustMap,
  finalize,
  map,
  of,
  ReplaySubject,
  share,
  withLatestFrom,
} from 'rxjs'

@Injectable()
export class ReferrersService
  extends AuthenticatedRestService
  implements AdvancedTableApiService<ReferrerModel>
{
  route = '/crud/referrers'
  collectionParams = new ReplaySubject<Record<string, any>>(1)
  updateParams = new ReplaySubject<Record<string, any>>(1)
  bust$ = new BehaviorSubject(false)

  private header$ = this.bust$.pipe(
    map((bust) => (bust ? new HttpHeaders({ 'x-refresh': 'true' }) : null)),
  )

  collectionResponse$ = this.collectionParams.pipe(
    map(truthyValues),
    map((object) => new HttpParams({ fromObject: object })),
    withLatestFrom(this.header$),
    concatMap(([params, headers]) =>
      // `email` is a referrer contact field the backend filters on; the REST client
      // blocks "sensitive" keys (email, phone, ...) in GET query strings by default,
      // so opt in like the shopper-estimates search does. Without this the guard
      // throws synchronously and permanently kills the collection stream.
      this.get<ReferrerModel[]>(this.route, params, { headers, allowSensitiveQueryParams: true }).pipe(
        catchError((error: HttpErrorResponse) => of(error)),
        finalize(() => this.bust$.next(false)),
      ),
    ),
    share(),
  )

  updateResponse$ = this.updateParams.pipe(
    map(truthyValues),
    exhaustMap((params) =>
      this.putOrPost(params).pipe(
        catchError((error: HttpErrorResponse) => of(error)),
        finalize(() => this.bust$.next(true)),
      ),
    ),
    share(),
  )

  private putOrPost = (params: Record<string, any>) => {
    if (params.id) {
      return this.put<ReferrerModel>(`${this.route}/${params.id}`, params)
    } else {
      return this.post<ReferrerModel>(this.route, params)
    }
  }
}
